import apis from '@api/api';
import { SHOW_LOADING_MODAL, HIDE_LOADING_MODAL } from '@reduxLocal/actions/types';
import type { Document, DocumentLayout, DocumentContent, DocumentDesign, DocumentCreationStage } from '../Components/DocumentWindow/types';
import { uniqueId } from 'lodash';
import { GiftedChat } from 'react-native-gifted-chat';

export interface DocumentHandlersConfig {
  dispatch: any;
  setChatMessages?: React.Dispatch<React.SetStateAction<any[]>>;
  setActiveWindowComponent?: (component: 'document' | null) => void;
  setDocument?: (document: Document | null) => void;
  setDocumentCreationStage?: (stage: DocumentCreationStage) => void;
  setPendingLayout?: (layout: DocumentLayout | null) => void;
  setPendingContent?: (content: DocumentContent | null) => void;
  globalUser?: any;
}

/**
 * Handle document creation - initiates the document creation workflow
 */
export const createHandleCreateDocument = (config: DocumentHandlersConfig) => {
  const {
    dispatch,
    setChatMessages,
    setActiveWindowComponent,
    setDocument,
    setDocumentCreationStage,
    setPendingLayout,
    globalUser,
  } = config;

  return async (documentType: string, title: string, description: string) => {
    try {
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Creating document layout...' } });
      setDocumentCreationStage('layout_planning');

      // Call layout planning endpoint
      const layoutResponse = await apis.createDocumentLayout(documentType, title, description);

      if (!layoutResponse.success || !layoutResponse.data) {
        throw new Error(layoutResponse.error || 'Failed to create document layout');
      }

      const layoutData: DocumentLayout = layoutResponse.data;

      // Store pending layout for user approval
      if (setPendingLayout) {
        setPendingLayout(layoutData);
      }

      // Open document window
      setActiveWindowComponent('document');

      // Add message to chat showing layout for approval
      const layoutMessageId = uniqueId();
      setChatMessages(prevMessages =>
        GiftedChat.append(prevMessages, [{
          _id: layoutMessageId,
          text: `I've created a ${layoutData.document_type} layout with ${layoutData.pages.length} pages. Please review and approve the structure.`,
          createdAt: new Date(),
          system: true,
          received: true,
          documentLayout: layoutData,
          requiresApproval: true,
          approvalType: 'layout'
        }])
      );

      setDocumentCreationStage('layout_approval');
      dispatch({ type: HIDE_LOADING_MODAL });
    } catch (error: any) {
      console.error('Error creating document layout:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      
      // Store retry handler
      const retryHandler = createHandleCreateDocument({
        dispatch,
        setChatMessages,
        setActiveWindowComponent,
        setDocument,
        setDocumentCreationStage,
        setPendingLayout,
        globalUser,
      });
      
      // Add error message to chat with retry functionality
      const errorMessageId = uniqueId();
      setChatMessages(prevMessages =>
        GiftedChat.append(prevMessages, [{
          _id: errorMessageId,
          text: `Sorry, I couldn't create the document layout. ${error?.message || 'Please try again.'}`,
          createdAt: new Date(),
          system: true,
          received: true,
          canRetry: true,
          retryFunction: () => {
            // Retry layout creation with the same parameters
            retryHandler(documentType, title, description);
          },
          retryData: { documentType, title, description }
        }])
      );
      
      setDocumentCreationStage('idle');
    }
  };
};

/**
 * Handle document layout approval - proceeds to content generation
 */
export const createHandleDocumentLayoutApproval = (config: DocumentHandlersConfig) => {
  const {
    dispatch,
    setChatMessages,
    setDocumentCreationStage,
    setPendingLayout,
    setPendingContent,
    globalUser,
  } = config;

  return async (layoutData: DocumentLayout, approved: boolean = true, userRequirements?: string) => {
    try {
      // If not approved, just clear the layout and go back to idle
      if (!approved) {
        if (setPendingLayout) {
          setPendingLayout(null);
        }
        setDocumentCreationStage('idle');
        
        setChatMessages(prevMessages =>
          GiftedChat.append(prevMessages, [{
            _id: uniqueId(),
            text: 'Layout rejected. Please provide feedback on what changes you\'d like, or create a new document.',
            createdAt: new Date(),
            system: true,
            received: true
          }])
        );
        return;
      }

      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Generating document content...' } });
      setDocumentCreationStage('content_generation');

      // Clear pending layout
      if (setPendingLayout) {
        setPendingLayout(null);
      }

      // Call content generation endpoint
      const contentResponse = await apis.generateDocumentContent(layoutData, userRequirements);

      if (!contentResponse.success || !contentResponse.data) {
        throw new Error(contentResponse.error || 'Failed to generate document content');
      }

      const contentData: DocumentContent = contentResponse.data;

      // Store pending content for user approval
      if (setPendingContent) {
        setPendingContent(contentData);
      }

      // Add message to chat showing content for approval
      const contentMessageId = uniqueId();
      setChatMessages(prevMessages =>
        GiftedChat.append(prevMessages, [{
          _id: contentMessageId,
          text: `I've generated content for all ${contentData.pages.length} pages. Please review and approve the content.`,
          createdAt: new Date(),
          system: true,
          received: true,
          documentContent: contentData,
          requiresApproval: true,
          approvalType: 'content'
        }])
      );

      setDocumentCreationStage('content_approval');
      dispatch({ type: HIDE_LOADING_MODAL });
    } catch (error: any) {
      console.error('Error generating document content:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      
      // Store retry handler
      const retryHandler = createHandleDocumentLayoutApproval({
        dispatch,
        setChatMessages,
        setActiveWindowComponent: config.setActiveWindowComponent || (() => {}),
        setDocument: config.setDocument || (() => {}),
        setDocumentCreationStage,
        setPendingLayout,
        setPendingContent,
        globalUser,
      });
      
      // Add error message to chat with retry functionality
      const errorMessageId = uniqueId();
      setChatMessages(prevMessages =>
        GiftedChat.append(prevMessages, [{
          _id: errorMessageId,
          text: `Sorry, I couldn't generate the document content. ${error?.message || 'Please try again.'}`,
          createdAt: new Date(),
          system: true,
          received: true,
          canRetry: true,
          retryFunction: () => {
            // Retry content generation with the same layout data
            retryHandler(layoutData, true, userRequirements || '');
          },
          retryData: { layoutData, userRequirements }
        }])
      );
      
      setDocumentCreationStage('layout_approval');
    }
  };
};

/**
 * Handle document content approval - proceeds to design refinement
 */
export const createHandleDocumentContentApproval = (config: DocumentHandlersConfig) => {
  const {
    dispatch,
    setChatMessages,
    setDocument,
    setDocumentCreationStage,
    setPendingContent,
    globalUser,
  } = config;

  return async (contentData: DocumentContent, designSettings?: Partial<DocumentDesign>) => {
    try {
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Applying design...' } });
      setDocumentCreationStage('design_refinement');

      // Clear pending content
      if (setPendingContent) {
        setPendingContent(null);
      }

      // Call design refinement endpoint
      const designResponse = await apis.refineDocumentDesign(contentData, designSettings);

      if (!designResponse.success || !designResponse.data) {
        throw new Error(designResponse.error || 'Failed to refine document design');
      }

      const designData = designResponse.data;

      // Create complete document object
      const document: Document = {
        document_id: designData.document_id || uniqueId(),
        content_id: designData.content_id || contentData.content_id,
        layout_id: contentData.layout_id,
        document_type: contentData.layout_id ? 'business_plan' : 'other', // Will be set from layout
        title: designData.title || 'Untitled Document',
        design_settings: designData.design_settings || {
          orientation: 'portrait',
        },
        pages: designData.pages || contentData.pages,
        validation: designData.validation,
      };

      // Set document in state
      setDocument(document);

      // Add success message to chat
      const successMessageId = uniqueId();
      setChatMessages(prevMessages =>
        GiftedChat.append(prevMessages, [{
          _id: successMessageId,
          text: `✅ Document created successfully! You can now edit, rearrange elements, and export your ${document.document_type}.`,
          createdAt: new Date(),
          system: true,
          received: true
        }])
      );

      setDocumentCreationStage('ready');
      dispatch({ type: HIDE_LOADING_MODAL });
    } catch (error: any) {
      console.error('Error refining document design:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      
      // Store retry handler
      const retryHandler = createHandleDocumentContentApproval({
        dispatch,
        setChatMessages,
        setActiveWindowComponent: config.setActiveWindowComponent || (() => {}),
        setDocument: config.setDocument || (() => {}),
        setDocumentCreationStage,
        setPendingContent,
        globalUser,
      });
      
      // Add error message to chat with retry functionality
      const errorMessageId = uniqueId();
      setChatMessages(prevMessages =>
        GiftedChat.append(prevMessages, [{
          _id: errorMessageId,
          text: `Sorry, I couldn't apply the design. ${error?.message || 'Please try again.'}`,
          createdAt: new Date(),
          system: true,
          received: true,
          canRetry: true,
          retryFunction: () => {
            // Retry design refinement with the same content data and design settings
            retryHandler(contentData, designSettings);
          },
          retryData: { contentData, designSettings }
        }])
      );
      
      setDocumentCreationStage('content_approval');
    }
  };
};

/**
 * Handle document design change - updates design settings (orientation, etc.)
 */
export const createHandleDocumentDesignChange = (config: DocumentHandlersConfig) => {
  const {
    dispatch,
    setDocument,
    globalUser,
  } = config;

  return async (document: Document, designChanges: Partial<DocumentDesign>) => {
    try {
      // If orientation changed, we need to regenerate design
      if (designChanges.orientation && designChanges.orientation !== document.design_settings.orientation) {
        dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Updating document design...' } });

        // Create content data from current document
        const contentData: DocumentContent = {
          content_id: document.content_id,
          layout_id: document.layout_id,
          pages: document.pages,
        };

        // Merge design settings
        const newDesignSettings: DocumentDesign = {
          ...document.design_settings,
          ...designChanges,
        };

        // Call design refinement with new settings
        const designResponse = await apis.refineDocumentDesign(contentData, newDesignSettings);

        if (!designResponse.success || !designResponse.data) {
          throw new Error(designResponse.error || 'Failed to update document design');
        }

        const designData = designResponse.data;

        // Update document with new design
        const updatedDocument: Document = {
          ...document,
          design_settings: designData.design_settings || newDesignSettings,
          pages: designData.pages || document.pages,
        };

        setDocument(updatedDocument);
        dispatch({ type: HIDE_LOADING_MODAL });
      } else {
        // Simple design update (colors, fonts, etc.) - no API call needed
        const updatedDocument: Document = {
          ...document,
          design_settings: {
            ...document.design_settings,
            ...designChanges,
          },
        };
        setDocument(updatedDocument);
      }
    } catch (error: any) {
      console.error('Error updating document design:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      throw error;
    }
  };
};


